import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { dashboardAPI, attendanceAPI, policyAPI } from '../../lib/api';
import { Avatar, AvatarFallback, AvatarImage } from '../../components/ui/avatar';
import { Button } from '../../components/ui/button';
import { Checkbox } from '../../components/ui/checkbox';
import { Label } from '../../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../../components/ui/dialog';
import { PdfViewer } from '../../components/ui/pdf-viewer';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Clock,
  CalendarCheck,
  Receipt,
  Building2,
  Briefcase,
  CalendarDays,
  Home,
  Check,
  AlertCircle,
  MapPin,
  LogOut,
  Loader2,
  ShieldAlert,
  Navigation,
  FileText,
  Download,
  Eye,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Normalize file URLs: ensure /uploads/ paths use /api/uploads/ for proper routing
const getFileUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http')) return url;
  // Ensure /uploads/ paths are prefixed with /api
  if (url.startsWith('/uploads/')) return `${BACKEND_URL}/api${url}`;
  if (url.startsWith('/api/uploads/')) return `${BACKEND_URL}${url}`;
  return `${BACKEND_URL}${url}`;
};

const STATUS_COLORS = {
  present: 'bg-green-500', absent: 'bg-red-500',
  half_day_morning: 'bg-yellow-500', half_day_afternoon: 'bg-yellow-500',
  on_leave: 'bg-blue-500', not_marked: 'bg-slate-300',
};

const STATUS_BADGE = {
  present: 'bg-green-100 text-green-700', absent: 'bg-red-100 text-red-700',
  half_day_morning: 'bg-yellow-100 text-yellow-700', half_day_afternoon: 'bg-yellow-100 text-yellow-700',
  on_leave: 'bg-blue-100 text-blue-700', not_marked: 'bg-slate-100 text-slate-500',
};

const STATUS_LABELS = {
  present: 'Present', absent: 'Absent',
  half_day_morning: 'Half Day (AM)', half_day_afternoon: 'Half Day (PM)',
  on_leave: 'On Leave', not_marked: 'Not Marked',
};

const CLAIM_STATUS_BADGE = {
  submitted: 'bg-yellow-100 text-yellow-700', under_review: 'bg-blue-100 text-blue-700',
  pending: 'bg-yellow-100 text-yellow-700', approved: 'bg-green-100 text-green-700',
  partially_approved: 'bg-orange-100 text-orange-700', rejected: 'bg-red-100 text-red-700',
  paid: 'bg-emerald-100 text-emerald-800',
};

// Reverse geocode using Nominatim (free, no key)
async function reverseGeocode(lat, lng) {
  try {
    const res = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=16`);
    const data = await res.json();
    const a = data.address;
    return a ? [a.road, a.neighbourhood, a.suburb, a.city || a.town || a.village].filter(Boolean).join(', ') : data.display_name?.split(',').slice(0, 3).join(',') || 'Unknown location';
  } catch { return 'Location captured'; }
}

function useGeolocation() {
  const [location, setLocation] = useState({ lat: null, lng: null, address: null, loading: false, error: null });

  const capture = async () => {
    if (!navigator.geolocation) {
      setLocation(l => ({ ...l, error: 'Geolocation not supported' }));
      return null;
    }
    setLocation(l => ({ ...l, loading: true, error: null }));
    return new Promise((resolve) => {
      navigator.geolocation.getCurrentPosition(
        async (pos) => {
          const { latitude, longitude } = pos.coords;
          const address = await reverseGeocode(latitude, longitude);
          const result = { lat: latitude, lng: longitude, address, loading: false, error: null };
          setLocation(result);
          resolve(result);
        },
        (err) => {
          const result = { lat: null, lng: null, address: null, loading: false, error: err.message };
          setLocation(result);
          resolve(result);
        },
        { enableHighAccuracy: true, timeout: 10000 }
      );
    });
  };

  return { ...location, capture };
}

const EmployeeHome = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [policies, setPolicies] = useState([]);
  const [viewingPolicy, setViewingPolicy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isMarkModalOpen, setIsMarkModalOpen] = useState(false);
  const [marking, setMarking] = useState(false);
  const [checkingOut, setCheckingOut] = useState(false);
  const geo = useGeolocation();

  const [markData, setMarkData] = useState({ status: 'present', is_wfh: false });

  useEffect(() => {
    dashboardAPI.getEmployee().then(r => setData(r.data)).catch(console.error).finally(() => setLoading(false));
    policyAPI.getAll().then(r => setPolicies(r.data)).catch(console.error);
  }, []);

  const refreshData = async () => {
    const r = await dashboardAPI.getEmployee();
    setData(r.data);
  };

  const handleMarkAttendance = async () => {
    setMarking(true);
    try {
      const loc = await geo.capture();
      await attendanceAPI.markAttendance({
        status: markData.status,
        is_wfh: markData.is_wfh,
        latitude: loc?.lat || null,
        longitude: loc?.lng || null,
        address: loc?.address || null,
      });
      await refreshData();
      setIsMarkModalOpen(false);
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to mark attendance');
    } finally {
      setMarking(false);
    }
  };

  const handleCheckout = async () => {
    if (!window.confirm('Check out for today? This will record the current time.')) return;
    setCheckingOut(true);
    try {
      const loc = await geo.capture();
      await attendanceAPI.checkout({
        latitude: loc?.lat || null,
        longitude: loc?.lng || null,
        address: loc?.address || null,
      });
      await refreshData();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to check out');
    } finally {
      setCheckingOut(false);
    }
  };

  const openMarkModal = () => {
    setMarkData({ status: 'present', is_wfh: false });
    setIsMarkModalOpen(true);
  };

  const getCompanyLabel = (c) => {
    switch (c) {
      case 'empact_studio': return 'Empact Studio';
      case 'event_resource': return 'Event Resource';
      case 'both': return 'Empact Studio & Event Resource';
      default: return c;
    }
  };

  const getInitials = (n) => n?.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2) || 'U';
  const getPhotoUrl = (p) => (!p ? null : p.startsWith('http') ? p : `${BACKEND_URL}${p}`);

  const todayAtt = data?.today_attendance;
  const todayStatus = todayAtt?.status || 'not_marked';
  const hasCheckedIn = todayAtt && todayAtt.check_in_time;
  const hasCheckedOut = todayAtt && todayAtt.check_out_time;

  // Build mini calendar
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const firstDay = new Date(year, month, 1).getDay();
  const attMap = {};
  (data?.month_attendance || []).forEach(a => {
    const day = parseInt(a.date.split('-')[2]);
    attMap[day] = a.status;
  });

  // Combine pending requests
  const pendingItems = [
    ...(data?.pending_leaves || []).map(l => ({ ...l, type: 'leave', label: l.leave_type_name, date: `${l.start_date} to ${l.end_date}` })),
    ...(data?.pending_claims || []).map(c => ({ ...c, type: 'claim', label: `Rs. ${c.amount_claimed.toLocaleString('en-IN')} - ${c.category}`, date: c.date_incurred })),
  ].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="loading-spinner">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1D35A0]" />
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="employee-portal">
      {/* Welcome Card */}
      <div className="bg-gradient-to-r from-[#1D35A0] to-[#1D35A0]/80 rounded-lg p-4 text-white">
        <div className="flex flex-col md:flex-row md:items-center gap-4">
          <Avatar className="w-20 h-20 border-4 border-white/20">
            <AvatarImage src={getPhotoUrl(user?.profile_photo)} />
            <AvatarFallback className="bg-white/20 text-white text-xl">{getInitials(user?.full_name)}</AvatarFallback>
          </Avatar>
          <div>
            <h1 className="text-2xl font-bold">Welcome, {user?.full_name}!</h1>
            <div className="flex flex-wrap items-center gap-4 mt-2 text-white/80 text-sm">
              <div className="flex items-center gap-1.5"><Briefcase className="w-4 h-4" />{user?.designation}</div>
              <div className="flex items-center gap-1.5"><Building2 className="w-4 h-4" />{getCompanyLabel(user?.company)}</div>
              <div className="flex items-center gap-1.5"><CalendarDays className="w-4 h-4" />Joined {user?.date_of_joining}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Today's Attendance Status Card */}
      <div className="bg-white rounded-lg border border-slate-200 p-3.5 shadow-sm">
        <div className="flex flex-col gap-4">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-8 h-8 rounded-lg bg-emerald-50 flex items-center justify-center">
                <Clock className="w-4 h-4 text-emerald-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-slate-500">Today's Attendance</p>
                <div className="flex flex-wrap items-center gap-3 mt-1">
                  {todayStatus !== 'not_marked' ? (
                    <>
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${STATUS_BADGE[todayStatus]}`}>
                        {STATUS_LABELS[todayStatus]}
                      </span>
                      {todayAtt?.is_wfh && <span className="flex items-center gap-1 text-sm text-blue-600"><Home className="w-4 h-4" /> WFH</span>}
                      {todayAtt?.is_late && <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-700">Late</span>}
                      {todayAtt?.check_in_time && <span className="text-sm text-slate-500">In: {todayAtt.check_in_time}</span>}
                      {todayAtt?.check_out_time && <span className="text-sm text-slate-500">Out: {todayAtt.check_out_time}</span>}
                    </>
                  ) : (
                    <span className="flex items-center gap-2 text-slate-500"><AlertCircle className="w-4 h-4" /> Not marked yet</span>
                  )}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {!hasCheckedIn && (
                <Button onClick={openMarkModal} className="bg-[#1D35A0] hover:bg-[#152A80] text-white" data-testid="mark-attendance-btn">
                  <Clock className="w-4 h-4 mr-2" /> Check In
                </Button>
              )}
              {hasCheckedIn && !hasCheckedOut && (
                <Button onClick={handleCheckout} disabled={checkingOut} variant="outline" className="border-red-300 text-red-600 hover:bg-red-50" data-testid="checkout-btn">
                  {checkingOut ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <LogOut className="w-4 h-4 mr-2" />}
                  Check Out
                </Button>
              )}
              {hasCheckedOut && (
                <span className="flex items-center gap-1.5 px-3 py-1.5 bg-green-50 text-green-700 rounded-full text-sm font-medium">
                  <Check className="w-4 h-4" /> Day complete
                </span>
              )}
            </div>
          </div>
          {/* Location info */}
          {todayAtt?.check_in_address && (
            <div className="flex flex-wrap items-center gap-4 text-xs text-slate-500 border-t border-slate-100 pt-3">
              <span className="flex items-center gap-1">
                <MapPin className="w-3.5 h-3.5 text-blue-500" />
                Check-in: {todayAtt.check_in_address}
                {todayAtt.is_outside_geofence && (
                  <span className="ml-1 px-1.5 py-0.5 bg-red-100 text-red-700 rounded text-[10px] font-medium flex items-center gap-0.5">
                    <ShieldAlert className="w-3 h-3" /> Outside office
                  </span>
                )}
                {todayAtt.is_outside_geofence === false && (
                  <span className="ml-1 px-1.5 py-0.5 bg-green-100 text-green-700 rounded text-[10px] font-medium">In-office</span>
                )}
              </span>
              {todayAtt.check_out_address && (
                <span className="flex items-center gap-1">
                  <MapPin className="w-3.5 h-3.5 text-orange-500" />
                  Check-out: {todayAtt.check_out_address}
                </span>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="quick-actions">
        <button onClick={!hasCheckedIn ? openMarkModal : undefined} className={`quick-action-btn border-emerald-300 hover:border-emerald-500 hover:bg-emerald-50 ${hasCheckedIn ? 'opacity-60' : ''}`} data-testid="quick-action-mark-attendance" disabled={hasCheckedIn}>
          <div className="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center">
            <Clock className="w-7 h-7 text-emerald-600" />
          </div>
          <div className="text-center">
            <p className="font-semibold text-[#1D35A0]">{hasCheckedIn ? 'Checked In' : 'Mark Attendance'}</p>
            <p className="text-sm text-slate-500">{hasCheckedIn ? todayAtt?.check_in_time : 'Auto-captures time & location'}</p>
          </div>
          {hasCheckedIn && <div className="absolute top-2 right-2"><Check className="w-5 h-5 text-green-500" /></div>}
        </button>

        <button onClick={() => navigate('/portal/leave')} className="quick-action-btn border-blue-300 hover:border-blue-500 hover:bg-blue-50" data-testid="quick-action-apply-for-leave">
          <div className="w-14 h-14 rounded-full bg-blue-100 flex items-center justify-center">
            <CalendarCheck className="w-7 h-7 text-blue-600" />
          </div>
          <div className="text-center">
            <p className="font-semibold text-[#1D35A0]">Apply for Leave</p>
            <p className="text-sm text-slate-500">Submit leave request</p>
          </div>
        </button>

        <button onClick={() => navigate('/portal/expenses')} className="quick-action-btn border-amber-300 hover:border-amber-500 hover:bg-amber-50" data-testid="quick-action-submit-expense-claim">
          <div className="w-14 h-14 rounded-full bg-amber-100 flex items-center justify-center">
            <Receipt className="w-7 h-7 text-amber-600" />
          </div>
          <div className="text-center">
            <p className="font-semibold text-[#1D35A0]">Submit Expense Claim</p>
            <p className="text-sm text-slate-500">File a reimbursement</p>
          </div>
        </button>
      </div>

      {/* Company Policies & Notices - Always visible */}
      <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-5" data-testid="policies-section">
        <h3 className="text-base font-semibold text-[#1D35A0] mb-3 flex items-center gap-2">
          <FileText className="w-4 h-4 text-blue-600" />
          Company Policies & Notices
        </h3>
        {policies.length > 0 ? (
          <div className="divide-y divide-slate-100">
            {policies.map(policy => (
              <div key={policy.id} className="flex items-center justify-between py-3 gap-3" data-testid={`employee-policy-${policy.id}`}>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-slate-800 truncate">{policy.title}</p>
                  <div className="flex items-center gap-2 mt-1">
                    {policy.description && <p className="text-xs text-slate-400 truncate max-w-[200px]">{policy.description}</p>}
                    <span className="text-xs text-slate-400">
                      {new Date(policy.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                    </span>
                  </div>
                </div>
              <div className="flex items-center gap-2 shrink-0">
                  <button
                    onClick={() => setViewingPolicy(policy)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
                    data-testid={`view-policy-${policy.id}`}
                  >
                    <Eye className="w-3.5 h-3.5" />
                    View
                  </button>
                  <a
                    href={getFileUrl(policy.file_url)}
                    download
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-slate-600 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
                    data-testid={`download-policy-${policy.id}`}
                  >
                    <Download className="w-3.5 h-3.5" />
                  </a>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-6 text-slate-400 text-sm" data-testid="no-policies-message">
            <FileText className="w-8 h-8 mx-auto mb-2 opacity-40" />
            No company policies or notices available yet
          </div>
        )}
      </div>

      {/* Two Cards: Mini Calendar + Leave Balance */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Mini Attendance Calendar */}
        <div className="bg-white rounded-lg border border-slate-200 p-3.5 shadow-sm" data-testid="mini-attendance-calendar">
          <h3 className="text-base font-semibold text-[#1D35A0] mb-3">My Attendance — {now.toLocaleString('default', { month: 'long', year: 'numeric' })}</h3>
          <div className="grid grid-cols-7 gap-1 text-center">
            {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d, i) => (
              <div key={i} className="text-xs font-semibold text-slate-400 py-1">{d}</div>
            ))}
            {Array.from({ length: firstDay }, (_, i) => <div key={`e-${i}`} />)}
            {Array.from({ length: daysInMonth }, (_, i) => {
              const day = i + 1;
              const status = attMap[day];
              const isToday = day === now.getDate();
              const dotColor = status ? STATUS_COLORS[status] : '';
              return (
                <div key={day} className={`relative py-1 rounded-md text-xs ${isToday ? 'ring-2 ring-blue-400 font-bold' : ''}`}>
                  {day}
                  {dotColor && <div className={`absolute bottom-0 left-1/2 -translate-x-1/2 w-1.5 h-1.5 rounded-full ${dotColor}`} />}
                </div>
              );
            })}
          </div>
          <div className="flex flex-wrap gap-3 mt-3 text-xs text-slate-500">
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-green-500" />Present</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-500" />Absent</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-yellow-500" />Half Day</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-blue-500" />On Leave</span>
          </div>
        </div>

        {/* Leave Balance */}
        <div className="bg-white rounded-lg border border-slate-200 p-3.5 shadow-sm" data-testid="leave-balance-card">
          <h3 className="text-base font-semibold text-[#1D35A0] mb-3">Leave Balance</h3>
          <div className="space-y-2 max-h-[250px] overflow-y-auto">
            {(data?.leave_balances || []).map(b => (
              <div key={b.id || b.leave_type_id} className="flex items-center justify-between py-2 px-3 bg-slate-50 rounded-lg">
                <span className="text-sm font-medium text-slate-700">{b.leave_type_name}</span>
                <span className="text-sm">
                  <span className="font-bold text-[#1D35A0]">{b.balance}</span>
                  <span className="text-slate-400"> / {b.entitlement}</span>
                </span>
              </div>
            ))}
            {(data?.leave_balances || []).length === 0 && (
              <p className="text-sm text-slate-400 text-center py-4">No leave balances configured yet</p>
            )}
          </div>
        </div>
      </div>

      {/* My Pending Requests */}
      <div className="bg-white rounded-lg border border-slate-200 shadow-sm" data-testid="my-pending-requests">
        <div className="px-5 py-4 border-b border-slate-200">
          <h3 className="text-base font-semibold text-[#1D35A0]">My Pending Requests</h3>
        </div>
        <div className="divide-y divide-slate-100 max-h-[300px] overflow-y-auto">
          {pendingItems.map(item => (
            <div key={item.id} className="px-5 py-3 flex items-center justify-between hover:bg-slate-50 transition-colors" data-testid={`pending-item-${item.id}`}>
              <div className="min-w-0">
                <p className="text-sm font-medium text-slate-800 truncate">{item.label}</p>
                <p className="text-xs text-slate-500">{item.date}</p>
              </div>
              <div className="flex items-center gap-2 ml-3 shrink-0">
                <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${item.type === 'leave' ? 'bg-blue-50 text-blue-600' : 'bg-amber-50 text-amber-600'}`}>
                  {item.type === 'leave' ? 'Leave' : 'Claim'}
                </span>
                <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${CLAIM_STATUS_BADGE[item.status] || 'bg-slate-100 text-slate-600'}`}>
                  {item.status === 'submitted' ? 'Submitted' : item.status === 'pending' ? 'Pending' : item.status}
                </span>
              </div>
            </div>
          ))}
          {pendingItems.length === 0 && (
            <div className="text-center py-8 text-slate-400 text-sm">
              <Check className="w-8 h-8 mx-auto mb-2 opacity-40" />
              No pending requests
            </div>
          )}
        </div>
      </div>

      {/* Mark Attendance Modal */}
      <Dialog open={isMarkModalOpen} onOpenChange={setIsMarkModalOpen}>
        <DialogContent className="max-w-md" data-testid="mark-attendance-modal">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-[#1D35A0]">Check In</DialogTitle>
            <DialogDescription className="text-slate-500">Your time and location will be captured automatically</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-700 flex items-center gap-2">
              <Navigation className="w-5 h-5 shrink-0" />
              <span>Time and GPS location will be recorded when you check in.</span>
            </div>
            <div className="space-y-2">
              <Label>Status *</Label>
              <Select value={markData.status} onValueChange={(v) => setMarkData(p => ({ ...p, status: v }))}>
                <SelectTrigger data-testid="select-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="present">Present</SelectItem>
                  <SelectItem value="absent">Absent</SelectItem>
                  <SelectItem value="half_day_morning">Half Day (Morning)</SelectItem>
                  <SelectItem value="half_day_afternoon">Half Day (Afternoon)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox id="is_wfh" checked={markData.is_wfh} onCheckedChange={(c) => setMarkData(p => ({ ...p, is_wfh: c }))} data-testid="checkbox-wfh" />
              <Label htmlFor="is_wfh" className="text-sm font-normal cursor-pointer"><Home className="w-4 h-4 inline mr-1 text-blue-500" />Work From Home (WFH)</Label>
            </div>
            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button variant="outline" onClick={() => setIsMarkModalOpen(false)}>Cancel</Button>
              <Button onClick={handleMarkAttendance} className="bg-[#1D35A0] hover:bg-[#152A80] text-white" disabled={marking} data-testid="submit-attendance-btn">
                {marking ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Capturing location...</> : <><MapPin className="w-4 h-4 mr-2" /> Check In Now</>}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Policy Viewer */}
      {viewingPolicy && (
        <Dialog open={!!viewingPolicy} onOpenChange={() => setViewingPolicy(null)}>
          <DialogContent className="max-w-2xl max-h-[90vh]" data-testid="policy-viewer-dialog">
            <DialogHeader>
              <DialogTitle>{viewingPolicy.title}</DialogTitle>
              <DialogDescription>{viewingPolicy.description || 'Company Policy Document'}</DialogDescription>
            </DialogHeader>
            <PdfViewer
              policyId={viewingPolicy.id}
              title={viewingPolicy.title}
            />
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default EmployeeHome;
